import { useCallback, useMemo } from 'react';

import { FetchCurrentQuery } from 'generated/graphql';
import { SHOW_NEW_PRICING } from 'types/featureFlag';

import { useCurrent } from './useCurrent';

/** When true, `SHOW_NEW_PRICING` checks always pass (local dev only). */
const forceNewPricingForDev =
  process.env.NODE_ENV === 'development' &&
  (process.env.REACT_APP_FORCE_NEW_PRICING === 'true' ||
    process.env.REACT_APP_FORCE_NEW_PRICING === '1');

export const hasSettings = (
  data: FetchCurrentQuery | undefined,
  ...settings: (string | ['user' | 'customer', string])[]
) => {
  if (!data) {
    return false;
  }

  const hasCustomerSetting = (name: string) => {
    const settingRecord = data.current.settings?.find((setting) => setting.name === name);

    if (settingRecord) {
      return settingRecord.value === 'true';
    }

    return data.current.customerSettings.includes(name);
  };

  return settings.reduce((acc, setting) => {
    if (Array.isArray(setting)) {
      const [store, name] = setting;

      if (store === 'user') {
        return acc && data.current.userFeatureFlags.includes(name);
      }

      return acc && hasCustomerSetting(name);
    } else {
      if (forceNewPricingForDev && setting === SHOW_NEW_PRICING) {
        return acc;
      }
      return (
        acc && (data.current.userFeatureFlags.includes(setting) || hasCustomerSetting(setting))
      );
    }
  }, true);
};

export function useHasSettings(
  ...settings: (string | ['user' | 'customer', string])[]
): [boolean, boolean] {
  const { data, loading } = useCurrent();

  const _hasSettings = useMemo(() => hasSettings(data, ...settings), [data, settings]);

  return [_hasSettings, loading] as [boolean, boolean];
}

export function useCheckSettings(): [
  (...settings: (string | ['user' | 'customer', string])[]) => boolean,
  boolean
] {
  const { data, loading } = useCurrent();
  const _hasSettings = useCallback(
    (...settings: (string | ['user' | 'customer', string])[]) => {
      return hasSettings(data, ...settings);
    },
    [data]
  );

  return [_hasSettings, loading];
}
